import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useRouteContext } from "@tanstack/react-router";
import Button from "react-bootstrap/Button";
import { FiArrowRight, FiHelpCircle, FiSave } from "react-icons/fi";
import {
  DropDownList,
  type DropDownListChangeEvent,
} from "@progress/kendo-react-dropdowns";

import Card from "@/components/shared/Card";
import LoaderButton from "@/components/shared/LoaderButton";
import TooltipTrigger from "@/components/shared/TooltipTrigger";
import TextInput from "@/components/shared/forms/TextInput";
import { FileSubmissionsContext } from "@/components/layout/FileSubmissionsTracker";
import { fetchAuthenticatedData } from "@/queries/shared";
import {
  ATTN_TEMPLATE,
  type ATTN,
  type DropDownModel,
  type GlobalStateModel,
  type LeaDataModel,
  type MutationResponse,
} from "@/models.ts";
import { asSerializeableATTN } from "@/models/chld";
import { useCoreData } from "@/queries.ts";
import {
  formatAttendanceMonth,
  getAttendanceMonthOptions,
  getLastDayOfMonth,
  useATTNFormLogic,
  validateDaysExpectedSum,
} from "@/components/records/child/useATTNFormLogic";
import BulkAttendanceGrid, {
  type BulkAttendanceRow,
  type BulkAttendanceRowErrors,
  type EditableAttendanceField,
} from "./BulkAttendanceGrid";

type PreschoolSummary = {
  PreschoolCDSCode: string;
  PreschoolName: string;
};

const NONE_SELECTED: DropDownModel = { Text: "None Selected", Value: "" };

function buildRows(
  leaData: LeaDataModel,
  programYear: string,
  preschoolCode: string,
  firstOfMonth: string,
  topDaysExpected: string
): BulkAttendanceRow[] {
  const monthStart = firstOfMonth;
  const monthEnd = getLastDayOfMonth(firstOfMonth);

  const existingByChild = new Map(
    leaData.attn
      .filter(
        (a) =>
          a.ProgramYear === programYear &&
          a.PreschoolCode === preschoolCode &&
          a.FirstOfMonth === firstOfMonth
      )
      .map((a) => [a.CSPPID, a])
  );

  return leaData.enrl
    .filter(
      (e) =>
        e.ProgramYear === programYear &&
        e.PreschoolCode === preschoolCode &&
        e.EnrollmentStartDate <= monthEnd &&
        (!e.EnrollmentEndDate || e.EnrollmentEndDate >= monthStart)
    )
    .map((e) => {
      const child = leaData.entities.children[e.CSPPID].records[0];
      const childName = [
        child.ChildFirstName,
        child.ChildMiddleName,
        child.ChildLastName,
      ]
        .filter(Boolean)
        .join(" ");
      const fullMonth =
        e.EnrollmentStartDate <= monthStart &&
        (!e.EnrollmentEndDate || e.EnrollmentEndDate >= monthEnd);
      const existing = existingByChild.get(e.CSPPID);

      return {
        CSPPID: e.CSPPID,
        LocalChildId: e.LocalChildId,
        ChildName: childName,
        EnrollmentStartDate: e.EnrollmentStartDate,
        EnrollmentEndDate: e.EnrollmentEndDate,
        fullMonth,
        hasExistingRecord: !!existing,
        DaysExpected: existing
          ? String(existing.DaysExpected)
          : fullMonth
            ? topDaysExpected
            : "",
        DaysAttended: existing ? String(existing.DaysAttended) : "",
        DaysAbsentExcused: existing ? String(existing.DaysAbsentExcused) : "",
        DaysAbsentUnexcused: existing
          ? String(existing.DaysAbsentUnexcused)
          : "",
        DaysAbsentSuspension: existing
          ? String(existing.DaysAbsentSuspension)
          : "",
        DaysAttendedSuspension: existing
          ? String(existing.DaysAttendedSuspension)
          : "",
      };
    });
}

function getCommonExistingDaysExpected(rows: BulkAttendanceRow[]): string {
  const counts = new Map<string, number>();
  for (const row of rows) {
    if (!row.hasExistingRecord) continue;
    counts.set(row.DaysExpected, (counts.get(row.DaysExpected) ?? 0) + 1);
  }

  let best = "";
  let bestCount = 0;
  for (const [value, count] of counts) {
    if (count > bestCount) {
      best = value;
      bestCount = count;
    }
  }
  return best;
}

function validateRow(
  row: BulkAttendanceRow,
  maxDaysExpected: number,
  validators: ReturnType<typeof useATTNFormLogic>["validators"]
): BulkAttendanceRowErrors {
  const errors: BulkAttendanceRowErrors = {};

  const attendedCheck = validators.validateNonNegativeInteger(row.DaysAttended);
  if (attendedCheck !== true) errors.DaysAttended = attendedCheck;

  const optionalFields: EditableAttendanceField[] = [
    "DaysAbsentExcused",
    "DaysAbsentUnexcused",
    "DaysAbsentSuspension",
    "DaysAttendedSuspension",
  ];
  for (const field of optionalFields) {
    const check = validators.validateOptionalNonNegativeInteger(row[field]);
    if (check !== true) errors[field] = check;
  }

  const integerCheck = validators.validateNonNegativeInteger(row.DaysExpected);
  if (integerCheck !== true) {
    errors.DaysExpected = integerCheck;
  } else if (Number(row.DaysExpected) > maxDaysExpected) {
    errors.DaysExpected = `Days Expected cannot exceed ${maxDaysExpected} for this month`;
  } else if (Object.keys(errors).length === 0) {
    const sumCheck = validateDaysExpectedSum({
      DaysExpected: Number(row.DaysExpected),
      DaysAttended: Number(row.DaysAttended),
      DaysAbsentExcused: Number(row.DaysAbsentExcused),
      DaysAbsentUnexcused: Number(row.DaysAbsentUnexcused),
      DaysAbsentSuspension: Number(row.DaysAbsentSuspension),
      DaysAttendedSuspension: Number(row.DaysAttendedSuspension),
    });
    if (sumCheck !== true) errors.DaysExpected = sumCheck;
  }

  return errors;
}

const EDITABLE_FIELDS: EditableAttendanceField[] = [
  "DaysExpected",
  "DaysAttended",
  "DaysAbsentExcused",
  "DaysAbsentUnexcused",
  "DaysAbsentSuspension",
  "DaysAttendedSuspension",
];

function isRowBlank(row: BulkAttendanceRow): boolean {
  return EDITABLE_FIELDS.every((field) => row[field].trim() === "");
}

export default function BulkAttendanceEntry({
  leaData,
}: {
  leaData: LeaDataModel;
}) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const coreData = useCoreData();
  const { addPendingFileSubmission, showToast } = useContext(
    FileSubmissionsContext
  );

  const canEdit = leaContext.hasRole("ATTNEdit");

  const [programYear, setProgramYear] = useState("");
  const [preschoolCode, setPreschoolCode] = useState("");
  const [firstOfMonth, setFirstOfMonth] = useState("");
  const [topDaysExpected, setTopDaysExpected] = useState("");
  const [topDaysExpectedError, setTopDaysExpectedError] = useState<
    string | undefined
  >();
  const [rows, setRows] = useState<BulkAttendanceRow[]>([]);
  const [touchedDaysExpected, setTouchedDaysExpected] = useState<Set<string>>(
    new Set()
  );
  const [rowErrors, setRowErrors] = useState<
    Record<string, BulkAttendanceRowErrors>
  >({});
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const [summary, setSummary] = useState<{
    succeeded: number;
    failed: string[];
  } | null>(null);

  const { maxDaysExpected, validators } = useATTNFormLogic(firstOfMonth);

  const validateTopDaysExpected = (value: string): string | undefined => {
    if (value.trim() === "") return undefined;
    const integerCheck = validators.validateNonNegativeInteger(value);
    if (integerCheck !== true) return integerCheck;
    const maxCheck = validators.validateDaysExpected.max(value);
    return maxCheck === true ? undefined : maxCheck;
  };

  const preschools: PreschoolSummary[] =
    leaContext.currentOrgPreschoolsByProgramYear(programYear);
  const monthOptions = getAttendanceMonthOptions(programYear);

  
  const siteCompletion = useMemo(() => {
    const map = new Map<string, { enrolled: number; posted: number }>();
    if (!programYear || !firstOfMonth) return map;
    for (const p of preschools) {
      const siteRows = buildRows(
        leaData,
        programYear,
        p.PreschoolCDSCode,
        firstOfMonth,
        ""
      );
      map.set(p.PreschoolCDSCode, {
        enrolled: siteRows.length,
        posted: siteRows.filter((r) => r.hasExistingRecord).length,
      });
    }
    return map;
  }, [leaData, programYear, firstOfMonth, preschools]);

  const sitesWithChildren = preschools.filter(
    (p) => (siteCompletion.get(p.PreschoolCDSCode)?.enrolled ?? 0) > 0
  );
  const incompleteSites = sitesWithChildren.filter((p) => {
    const c = siteCompletion.get(p.PreschoolCDSCode)!;
    return c.posted < c.enrolled;
  });

  const handleNextIncompleteSite = () => {
    if (incompleteSites.length === 0) return;
    const currentIndex = preschools.findIndex(
      (p) => p.PreschoolCDSCode === preschoolCode
    );
    const next =
      incompleteSites.find(
        (p) => preschools.findIndex((x) => x === p) > currentIndex
      ) ?? incompleteSites[0];
    setPreschoolCode(next.PreschoolCDSCode);
  };

  const topDaysExpectedRef = useRef(topDaysExpected);
  topDaysExpectedRef.current = topDaysExpected;
  const lastBuiltSelectionRef = useRef<string | null>(null);
  const justSubmittedCsppidsRef = useRef<Set<string>>(new Set());
  const userSetTopDaysExpectedRef = useRef(false);

  useEffect(() => {
    setRowErrors({});
    setSummary(null);
    setSubmitting(false);
    setProgress(null);
    setTopDaysExpectedError(undefined);
    if (!programYear || !preschoolCode || !firstOfMonth) {
      setRows([]);
      setTouchedDaysExpected(new Set());
      lastBuiltSelectionRef.current = null;
      return;
    }

    const selectionKey = `${programYear}|${preschoolCode}|${firstOfMonth}`;
    const isNewSelection = lastBuiltSelectionRef.current !== selectionKey;
    lastBuiltSelectionRef.current = selectionKey;

    if (isNewSelection) {
      const freshRows = buildRows(
        leaData,
        programYear,
        preschoolCode,
        firstOfMonth,
        ""
      );
      setRows(freshRows);
      setTouchedDaysExpected(new Set());
      userSetTopDaysExpectedRef.current = false;
      const commonDaysExpected = getCommonExistingDaysExpected(freshRows);
      setTopDaysExpected(commonDaysExpected);
      setTopDaysExpectedError(validateTopDaysExpected(commonDaysExpected));
      return;
    }

    const freshRows = buildRows(
      leaData,
      programYear,
      preschoolCode,
      firstOfMonth,
      topDaysExpectedRef.current
    );

    const justSubmitted = justSubmittedCsppidsRef.current;
    justSubmittedCsppidsRef.current = new Set();

    setRows((prevRows) => {
      const prevByChild = new Map(prevRows.map((row) => [row.CSPPID, row]));
      return freshRows.map((freshRow) => {
        const prevRow = prevByChild.get(freshRow.CSPPID);
        if (!prevRow) return freshRow;
        if (
          justSubmitted.has(freshRow.CSPPID) ||
          (freshRow.hasExistingRecord && !prevRow.hasExistingRecord)
        ) {
          return freshRow;
        }
        return prevRow;
      });
    });
  }, [leaData, programYear, preschoolCode, firstOfMonth]);

  useEffect(() => {
    if (topDaysExpectedError) return;
    setRows((prev) =>
      prev.map((row) => {
        if (
          !row.fullMonth ||
          touchedDaysExpected.has(row.CSPPID) ||
          !userSetTopDaysExpectedRef.current
        ) {
          return row;
        }
        if (topDaysExpected === "") {
          return {
            ...row,
            DaysExpected: "",
            DaysAbsentExcused: row.DaysAbsentExcused === "0" ? "" : row.DaysAbsentExcused,
            DaysAbsentUnexcused:
              row.DaysAbsentUnexcused === "0" ? "" : row.DaysAbsentUnexcused,
            DaysAbsentSuspension:
              row.DaysAbsentSuspension === "0" ? "" : row.DaysAbsentSuspension,
            DaysAttendedSuspension:
              row.DaysAttendedSuspension === "0" ? "" : row.DaysAttendedSuspension,
          };
        }
        return {
          ...row,
          DaysExpected: topDaysExpected,
          DaysAbsentExcused: row.DaysAbsentExcused === "" ? "0" : row.DaysAbsentExcused,
          DaysAbsentUnexcused:
            row.DaysAbsentUnexcused === "" ? "0" : row.DaysAbsentUnexcused,
          DaysAbsentSuspension:
            row.DaysAbsentSuspension === "" ? "0" : row.DaysAbsentSuspension,
          DaysAttendedSuspension:
            row.DaysAttendedSuspension === "" ? "0" : row.DaysAttendedSuspension,
        };
      })
    );
  }, [topDaysExpected, touchedDaysExpected, topDaysExpectedError]);

  const handleCellChange = (
    csppid: string,
    field: EditableAttendanceField,
    value: string
  ) => {
    if (field === "DaysExpected") {
      setTouchedDaysExpected((prev) => new Set(prev).add(csppid));
    }

    const newRows = rows.map((row) =>
      row.CSPPID === csppid ? { ...row, [field]: value } : row
    );
    setRows(newRows);

    const updatedRow = newRows.find((row) => row.CSPPID === csppid);
    if (updatedRow) {
      const errors = isRowBlank(updatedRow)
        ? {}
        : validateRow(
            updatedRow,
            maxDaysExpected,
            validators
          );
      setRowErrors((prevErrors) => {
        if (Object.keys(errors).length === 0) {
          if (!(csppid in prevErrors)) return prevErrors;
          const rest = { ...prevErrors };
          delete rest[csppid];
          return rest;
        }
        return { ...prevErrors, [csppid]: errors };
      });
    }
  };

  const rowsToSubmit = rows.filter((row) => !isRowBlank(row));

  const handleSubmit = async () => {
    if (submitting || rowsToSubmit.length === 0) return;

    const nextErrors: Record<string, BulkAttendanceRowErrors> = {};
    for (const row of rowsToSubmit) {
      const errors = validateRow(
        row,
        maxDaysExpected,
        validators
      );
      if (Object.keys(errors).length > 0) {
        nextErrors[row.CSPPID] = errors;
      }
    }
    setRowErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSubmitting(true);
    setProgress({ done: 0, total: rowsToSubmit.length });
    const failed: string[] = [];

    for (const [index, row] of rowsToSubmit.entries()) {
      const record: ATTN = {
        ...ATTN_TEMPLATE,
        CSPPID: row.CSPPID,
        LocalChildId: row.LocalChildId,
        ProgramYear: programYear,
        PreschoolCode: preschoolCode,
        FirstOfMonth: firstOfMonth,
        DaysExpected: Number(row.DaysExpected),
        DaysAttended: Number(row.DaysAttended),
        DaysAbsentExcused: Number(row.DaysAbsentExcused),
        DaysAbsentUnexcused: Number(row.DaysAbsentUnexcused),
        DaysAbsentSuspension: Number(row.DaysAbsentSuspension),
        DaysAttendedSuspension: Number(row.DaysAttendedSuspension),
      };

      try {
        const urlVal = `/${leaContext.currentUserOrg.esiIdPath}/dashboard/A/ajax/EditATTNAjax`;
        const resp = await fetchAuthenticatedData<MutationResponse<ATTN>>(
          leaContext as GlobalStateModel,
          urlVal,
          {
            method: "POST",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            body: JSON.stringify(asSerializeableATTN(record)),
          }
        );

        if (resp.serverError || resp.fileSubmission === null) {
          failed.push(row.ChildName);
        } else {
          justSubmittedCsppidsRef.current.add(row.CSPPID);
          const isLastRow = index === rowsToSubmit.length - 1;
          addPendingFileSubmission(
            resp.fileSubmission,
            isLastRow
              ? `Attendance submitted for ${rowsToSubmit.length} ${rowsToSubmit.length === 1 ? "child" : "children"}.`
              : undefined
          );
        }
      } catch {
        failed.push(row.ChildName);
      }

      setProgress({ done: index + 1, total: rowsToSubmit.length });
    }

    if (failed.length === rowsToSubmit.length) {
      setSubmitting(false);
    }
    setSummary({ succeeded: rowsToSubmit.length - failed.length, failed });

    if (failed.length === 0) {
      showToast(
        "processing",
        "Processing",
        `Submitted attendance for ${rowsToSubmit.length} children. Waiting for validation...`,
        "processing-attn-bulk"
      );
    } else {
      showToast(
        "error",
        "Error",
        `${failed.length} of ${rowsToSubmit.length} attendance records failed to submit: ${failed.join(", ")}`,
        "error-attn-bulk"
      );
    }
  };

  const handleReset = () => {
    setProgramYear("");
    setPreschoolCode("");
    setFirstOfMonth("");
    setTopDaysExpected("");
    setTopDaysExpectedError(undefined);
    setRows([]);
    setTouchedDaysExpected(new Set());
    setRowErrors({});
    setProgress(null);
    setSummary(null);
    setSubmitting(false);
  };

  const topDaysExpectedInput = (
    <TextInput
      id="bulkAttnTopDaysExpected"
      aria-labelledby="bulkAttnTopDaysExpectedLabel"
      aria-describedby="bulkAttnTopDaysExpectedDescription"
      type="number"
      className={
        topDaysExpectedError
          ? "child-search-control is-invalid"
          : "child-search-control"
      }
      value={topDaysExpected}
      disabled={!firstOfMonth || submitting}
      onChange={(e) => {
        const value = e.target.value;
        userSetTopDaysExpectedRef.current = true;
        setTopDaysExpected(value);
        setTopDaysExpectedError(validateTopDaysExpected(value));
      }}
    />
  );

  return (
    <Card className="card-custom mb-3 shadow-none">
      <div className="mb-0 p-4 bg-white w-100 child-search-form">
        <div className="row g-3">
          <div className="col-12 col-md-6">
            <label
              id="bulkAttnProgramYearLabel"
              className="form-label fw-semibold"
            >
              <i className="bi bi-calendar me-1" aria-hidden="true"></i> Program
              Year
            </label>
            <DropDownList
              id="bulkAttnProgramYear"
              ariaLabelledBy="bulkAttnProgramYearLabel"
              className="child-search-control"
              style={{ width: "100%" }}
              data={coreData.programYearList}
              defaultItem={NONE_SELECTED}
              textField="Text"
              dataItemKey="Value"
              value={
                coreData.programYearList.find((p) => p.Value === programYear) ??
                NONE_SELECTED
              }
              disabled={submitting}
              onChange={(e: DropDownListChangeEvent) => {
                setProgramYear((e.value as DropDownModel).Value);
                setPreschoolCode("");
                setFirstOfMonth("");
              }}
            />
          </div>
          <div className="col-12 col-md-6">
            <label id="bulkAttnMonthLabel" className="form-label fw-semibold">
              <i className="bi bi-calendar me-1" aria-hidden="true"></i> Month
            </label>
            <DropDownList
              id="bulkAttnMonth"
              ariaLabelledBy="bulkAttnMonthLabel"
              className="child-search-control"
              style={{ width: "100%" }}
              data={monthOptions}
              defaultItem={NONE_SELECTED}
              textField="Text"
              dataItemKey="Value"
              value={
                monthOptions.find((m) => m.Value === firstOfMonth) ??
                NONE_SELECTED
              }
              disabled={!programYear || submitting}
              onChange={(e: DropDownListChangeEvent) => {
                setFirstOfMonth((e.value as DropDownModel).Value);
                setPreschoolCode("");
              }}
            />
          </div>

          <div className="col-12 col-md-6">
            <label
              id="bulkAttnPreschoolLabel"
              className="form-label fw-semibold"
            >
              <i className="bi bi-building me-1" aria-hidden="true"></i>{" "}
              Preschool
            </label>
            <DropDownList
              id="bulkAttnPreschool"
              ariaLabelledBy="bulkAttnPreschoolLabel"
              className="child-search-control"
              style={{ width: "100%" }}
              data={preschools.map((p) => ({
                Text: p.PreschoolName,
                Value: p.PreschoolCDSCode,
              }))}
              defaultItem={NONE_SELECTED}
              textField="Text"
              dataItemKey="Value"
              value={
                preschools
                  .map((p) => ({
                    Text: p.PreschoolName,
                    Value: p.PreschoolCDSCode,
                  }))
                  .find((p) => p.Value === preschoolCode) ?? NONE_SELECTED
              }
              disabled={!firstOfMonth || submitting}
              onChange={(e: DropDownListChangeEvent) => {
                setPreschoolCode((e.value as DropDownModel).Value);
              }}
            />
          </div>
          <div className="col-12 col-md-6">
            <label
              id="bulkAttnTopDaysExpectedLabel"
              className="form-label fw-semibold"
            >
              <i className="bi bi-hash me-1" aria-hidden="true"></i> Default
              Days Expected (Optional)
              <TooltipTrigger tooltipText="Applied as a default value to every child enrolled for the full month; each row stays editable.">
                <FiHelpCircle
                  size={16}
                  className="text-muted"
                  style={{ marginLeft: 6, cursor: "pointer" }}
                  aria-hidden="true"
                />
              </TooltipTrigger>
            </label>
            <span id="bulkAttnTopDaysExpectedDescription" className="visually-hidden">
              Applied as a default value to every child enrolled for the full month; each row stays editable.
            </span>
            {topDaysExpectedError ? (
              <TooltipTrigger
                tooltipText={topDaysExpectedError}
                trigger={["hover", "focus"]}
              >
                {topDaysExpectedInput}
              </TooltipTrigger>
            ) : (
              topDaysExpectedInput
            )}
          </div>

          {firstOfMonth && sitesWithChildren.length > 0 && (
            <div className="col-12 d-flex align-items-center gap-3">
              {incompleteSites.length > 0 ? (
                <>
                  <span>
                    <strong>{incompleteSites.length}</strong> of{" "}
                    {sitesWithChildren.length} sites still need attendance for{" "}
                    {formatAttendanceMonth(firstOfMonth)}.
                  </span>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="d-flex align-items-center gap-2"
                    disabled={submitting}
                    onClick={handleNextIncompleteSite}
                  >
                    Next Incomplete Site
                    <FiArrowRight aria-hidden="true" />
                  </Button>
                </>
              ) : (
                <span className="text-success">
                  All {sitesWithChildren.length} sites have attendance entered
                  for {formatAttendanceMonth(firstOfMonth)}.
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {firstOfMonth && (
        <>
          <BulkAttendanceGrid
            rows={rows}
            rowErrors={rowErrors}
            onCellChange={handleCellChange}
            disabled={submitting || !canEdit}
          />

          {progress && (
            <p className="mt-3 mb-0">
              Submitting {progress.done} of {progress.total}...
            </p>
          )}

          {summary && summary.failed.length > 0 && (
            <p className="mt-3 mb-0 text-danger">
              {summary.succeeded} of {summary.succeeded + summary.failed.length}{" "}
              submitted successfully. Failed: {summary.failed.join(", ")}
            </p>
          )}

          <div className="d-flex gap-2 mt-3">
            {canEdit && (
              <LoaderButton
                variant="primary"
                className="d-flex align-items-center gap-2"
                disabled={rowsToSubmit.length === 0}
                loading={submitting}
                loadingText="Submitting..."
                onClick={() => {
                  void handleSubmit();
                }}
              >
                <FiSave aria-hidden="true" />
                Submit
              </LoaderButton>
            )}
            <Button
              variant="outline-secondary"
              disabled={submitting}
              onClick={handleReset}
            >
              Reset
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}
